import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import FacilityItem from "app/routes/Facility/FacilityItem";
import { fetchGet, fetchPut } from "app/utils/request";
import { toast } from "react-toastify";
import FacilityPill from "./FacilityPill";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
} from "app/utils/request/scheduling/fetch";
import { setSchedulingData } from "app/actions/user";
import {
  FacilityModel,
  FacilityResponse,
} from "app/models/scheduling/FacilityModel";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import TogglePill from "./TogglePill";
import LogoContainer from "./LogoContainer";
import RoundButton from "app/components/Programs/Buttons/RoundButton";
import PreviewEmail from "./PreviewEmail";
import { Loading } from "app/components/Wrappers";
import Tabs from "app/components/Tabs";
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { useHistory } from "react-router-dom";
import ToggleButtons from "./ToggleButtons";
import StartTimeIncrement from "./StartTimeIncrement";
import { UserContext } from "app/contexts/UserContext";
import { fetchAcademySetup } from "app/actions/academySetup";
import { useActions } from "app/utils/hooks";
import useAcademySetupHint from "app/utils/hooks/useAcademySetupHint";
import SetupHint from "app/components/AcademySetup/SetupHint";
import {
  isSetupItemOpen,
  showFacilityProfileSavedToast,
  showPublicPresenceToast,
} from "app/components/AcademySetup/SetupToast";
import { isAcademyOnboardingCoach } from "app/utils/subscription";

const Facility = () => {
  const { t } = useTranslation();
  const [facilities, setFacilities] = useState([]);
  const [load, setLoad] = useState(true);
  const currentFacility = useCurrentFacility();
  const tabs = useAdminSettingsTabs("general");
  const [previewBody, setPreviewBody] = useState(null);
  const dispatch = useDispatch();
  const [isLandingPageAlertOpen, setLandingAlert] = useState<boolean>(false);
  const history = useHistory();
  // Second step of the academy_page checklist item: the landing page hands
  // the hint here after its save so the coach finds the switch that
  // actually publishes the page.
  const landingSwitchRef = React.useRef<HTMLDivElement>(null);
  const landingSetupHint = useAcademySetupHint("academy_page");
  // First checklist item: the coachmark points at the Facility card's Edit
  // button, where the name and address the item checks are changed.
  const facilityEditRef = React.useRef<HTMLButtonElement>(null);
  const facilitySetupHint = useAcademySetupHint("facility_profile");
  const user = useContext(UserContext);
  const self = useSelector((state: RootState) => state.user.self);
  const academySetupActions = useActions({ fetchAcademySetup });

  // CN-2533: the facility scheduling interval (recommended default for coaches),
  // now editable here instead of the CoachNow admin panel.
  const facilityFlags = currentFacility.data?.featureFlags;
  const [schedulingInterval, setSchedulingInterval] = useState<number | null>(
    null,
  );
  const [savingInterval, setSavingInterval] = useState(false);

  useEffect(() => {
    setSchedulingInterval(facilityFlags?.schedulingInterval ?? null);
  }, [facilityFlags?.schedulingInterval]);

  const saveSchedulingInterval = (value: number) => {
    // Disable the control while saving (below) so a second change can't race an
    // in-flight PATCH and let a stale response/rollback overwrite the newer value.
    const previous = schedulingInterval;
    setSchedulingInterval(value);
    setSavingInterval(true);
    schedulingFetchPatch(
      `facilities/${currentFacility.data?.id}/update_scheduling_interval`,
      { scheduling_interval: value },
    )
      .then((response: FacilityResponse) => {
        dispatch(setSchedulingData(new FacilityModel(response)));
        toast.success(t("Update successful."));
      })
      .catch(() => {
        // Restore the prior value so the control doesn't imply an unsaved change.
        setSchedulingInterval(previous);
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => {
        setSavingInterval(false);
      });
  };

  useEffect(() => {
    fetchGet("/facilities", {}, { version: 1 })
      .then((response) => {
        setFacilities(response);
      })
      .catch((error) => {
        console.error(error);
      }).finally(() => {
        setLoad(false);
      });
  }, []);

  // `notify` false leaves the confirmation to the caller: the landing-page
  // toggle saves several fields in one action and wants one toast, not one per
  // field.
  const handlePublicCalendarChange = async (
    field_name,
    field_value,
    notify: boolean = true,
  ) => {
    const data = {
      public_field_name: field_name,
      public_field_value: field_value,
    };
    return schedulingFetchPatch(
      `facilities/${currentFacility.data?.id}/update_public_field`,
      data,
    )
      .then(async (response: FacilityResponse) => {
        dispatch(setSchedulingData(new FacilityModel(response)));
        if (notify) {
          toast.success(t("Update successful."));
        }
        return response;
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
        throw new Error();
      });
  };

  const handleEnableLandingPage = async (value: boolean) => {
    return fetchPut(`facilities/${currentFacility?.data?.id}`, {facility: { public_landing_page_enabled: value }})
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
        throw new Error();
      });
  };

  const getEmail = () => {
    schedulingFetchGet(
      `facilities/${currentFacility.data?.id}/email_preview`,
    ).then((response) => {
      setPreviewBody(response.preview);
    });
  };

  const updateFacilityLogo = (url) => {
    const data = {
      logo_url: url,
    };
    schedulingFetchPatch(
      `facilities/${currentFacility.data?.id}/update_logo`,
      data,
    ).then(async (response: FacilityResponse) => {
      dispatch(setSchedulingData(new FacilityModel(response)));
      toast.success(t("Update successful."));
    });
  };

  // Only the Academy account's coach has a checklist to refresh; anyone else editing
  // their facility must not trigger GET /academy/setup.
  const tracksSetup = isAcademyOnboardingCoach(self);

  const handleEditFacility = (facility) => {
    // Read before the save: the rail celebrates an item this save completed,
    // not one that was already done — and only for the coach's own facility,
    // the record the checklist's flag is computed from.
    const completes =
      tracksSetup &&
      facility.id === currentFacility.data?.id &&
      isSetupItemOpen("facility_profile");
    fetchPut(`/facilities/${facility.id}`, { facility }, { version: 1 })
      .then(async () => {
        setFacilities((prevFacilities) =>
          prevFacilities.map((f) => (f.id === facility.id ? facility : f)),
        );
        if (facilitySetupHint.active) {
          facilitySetupHint.dismiss();
        }
        // The saved name/address may have completed the facility_profile
        // item; the rail refetches and shows only if the API agrees, and the
        // generic toast covers every other save.
        const celebrated = completes
          ? await showFacilityProfileSavedToast(
              academySetupActions.fetchAcademySetup,
              (path, state) => history.push(path, state),
            )
          : false;
        if (!celebrated) {
          toast.success(t("Facility updated successfully"));
        }
        if (tracksSetup && !celebrated) {
          academySetupActions.fetchAcademySetup(true);
        }
      })
      .catch((error) => {
        toast.error(t("Failed to update facility"));
        console.error(error);
      });
  };

  const handleAllowAdditionalPeople = (allowAdditionalPeople: boolean) => {
    const data = {
      allow_additional_people: allowAdditionalPeople,
    };
    schedulingFetchPatch(
      `facilities/${currentFacility.data?.id}/update_allow_additional_people`,
      data,
    ).then(async (response: FacilityResponse) => {
      dispatch(setSchedulingData(new FacilityModel(response)));
      toast.success(t("Update successful."));
    });
  };

  const completedLandingPage = (facility) => {
    return facility.description && facility.phone && facility.email && facility.website;
  };



  const onClickLandingToggle = async (facility, value: boolean) => {
    if (value === false) {
      await handleEnableLandingPage(value);
      await handlePublicCalendarChange("public_landing_page", value);
      // academy_page is an API flag: without a refetch the checklist and the
      // sidebar count keep showing the page as published.
      if (tracksSetup) {
        academySetupActions.fetchAcademySetup();
      }
    } else {
      if (completedLandingPage(facility)) {
        if (landingSetupHint.active) {
          landingSetupHint.dismiss();
        }
        // Read before the saves, as in handleEditFacility: re-enabling a page
        // the checklist already counts must not re-celebrate it.
        const completes =
          tracksSetup &&
          facility.id === currentFacility.data?.id &&
          isSetupItemOpen("academy_page");
        await handleEnableLandingPage(value);
        await handlePublicCalendarChange("public_landing_page", value, false);
        await handlePublicCalendarChange("public_calendar", value, false);
        // One confirmation for the whole action: the Academy rail when this
        // completes the checklist item, the generic toast otherwise.
        const celebrated =
          completes &&
          (await showPublicPresenceToast(
            academySetupActions.fetchAcademySetup,
            (path, state) => history.push(path, state),
          ));
        if (!celebrated) {
          toast.success(t("Update successful."));
        }
      } else {
        setLandingAlert(true);
      }
    }
  };

  const onClickLandingPage = () => {
    history.push('/manage_facility/landing_page');
  };

  // The coachmarks and the refs they anchor to belong to one row only: a ref
  // shared across the map would settle on the last row rendered, and every
  // row would mount its own Coachmark against that same anchor. Prefer the
  // coach's current facility (the one the toggles in each row already edit),
  // else the first row.
  const hintFacilityId =
    facilities.find((f) => f.id === currentFacility.data?.id)?.id ??
    facilities[0]?.id;

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        <PagePanel title={t("General")} titleStyles={styles.header}>
          {load ? <Loading isLoading={load} loadType='spinner' /> :
            facilities.map((facility) => {
            const anchorsHints = facility.id === hintFacilityId;
            return (
            <div className={styles.row} key={facility.id}>
              {anchorsHints && (
                <SetupHint
                  itemKey="facility_profile"
                  anchorRef={facilityEditRef}
                  placement="bottom-end"
                  arrowAtAnchorCenter
                />
              )}
              <FacilityItem
                facility={facility}
                onEdit={handleEditFacility}
                editButtonRef={anchorsHints ? facilityEditRef : undefined}
              />
              <TogglePill
                onChange={(value) =>
                  handlePublicCalendarChange("public_calendar", value)
                }
                enabled={currentFacility?.data?.publicCalendar}
                inputText={currentFacility?.data?.publicCalendarURL}
                title={t("Public Facility Calendar")}
                inputLabel={t("Enable Public Calendar")}
                description={t(
                  "Copy the URL, so you can link to it from your website. Anyone who accesses the link will see the availability of all coaches and be able to book sessions.",
                )}
              />
              <FacilityPill
                header={t("TM Master Roster Sync")}
                categories={[
                  {
                    title: t("TM Master Roster Sync Enabled"),
                    text: facility.tmApiKey ? t("Yes") : t("No"),
                  },
                  {
                    title: t("Last Master Roster Sync"),
                    text: facility.tmMasterRosterSyncedAt || t("N/A"),
                  },
                ]}
              />
              {anchorsHints && (
                <SetupHint
                  itemKey="academy_page"
                  anchorRef={landingSwitchRef}
                  placement="bottom-start"
                  titleKey="Public Facility Landing Page"
                  bodyKey="Turn this on to publish the page you just saved and complete this step."
                />
              )}
              <TogglePill
                onChange={(value) =>
                  onClickLandingToggle(facility, value)
                }
                enabled={currentFacility?.data?.publicLandingPage}
                inputText={currentFacility?.data?.publicLandingPageURL}
                title={t("Public Facility Landing Page")}
                inputLabel={t("Enable Public Facility Landing Page")}
                description={t(
                  "Copy the URL, so you can link to it from your website. Anyone who accesses the link will see the landing page of the facility.",
                )}
                switchRowRef={anchorsHints ? landingSwitchRef : undefined}
              />
              <LogoContainer
                title={t("Email Branding")}
                label={t(
                  "Add your logo image to be included in email communications to your athletes.",
                )}
                footer={t(
                  "The logo must have a height of at least 80 pixels and no more than 150 pixels.",
                )}
                imageUrl={currentFacility?.data?.logo_url}
                setImageUrl={updateFacilityLogo}
              >
                <RoundButton
                  customStyle={styles.preview}
                  onClick={() => getEmail()}
                >
                  <p className={styles.preview_text}>{t("Preview Email")}</p>
                </RoundButton>
              </LogoContainer>
              <TogglePill
                onChange={(value) =>
                  handlePublicCalendarChange("public_programs", value)
                }
                enabled={currentFacility?.data?.publicPrograms}
                inputText={currentFacility?.data?.publicProgramsURL}
                titleToolTipText={[
                  t(
                    "Public programs allow athletes who are not already connected to your facility to register.",
                  ),
                  t(
                    "Public programs will also be listed on the Public Programs page, if enabled.",
                  ),
                ]}
                title={t("Public Programs")}
                inputLabel={t("Enable Public List of Programs")}
                description={t(
                  "Copy the URL, so you can link to it from your website. Anyone who accesses the link will see the list of programs marked as public, view the details of those programs and register.",
                )}
              />
              <ToggleButtons
                onChange={(value) =>
                  handleAllowAdditionalPeople(value)
                }
                title={t("Program Registration Restrictions")}
                description={t(
                  "Would you like to allow your clients to view and register additional people on their behalf?",
                )}
                secondDesc={t(
                  "Select “Do Not Allow” if you only want people to book for themselves. This restriction applies to parents, who will need to log into their child’s account to book on their children’s behalf."
                )}
                textFirstBtn={t("Allow")}
                textSecondBtn={t("Do Not Allow")}
                defaultValue={currentFacility.data?.allowAdditionalPeople}
              />
              {user.behavesLikeAdmin && (
                <StartTimeIncrement
                  value={schedulingInterval}
                  disabled={savingInterval}
                  onChange={saveSchedulingInterval}
                />
              )}
            </div>
            );
          })}
        </PagePanel>
      </div>
      {previewBody && (
        <PreviewEmail
          email={previewBody}
          closePreview={() => setPreviewBody(null)}
        />
      )}
      <ModalLayout isOpen={isLandingPageAlertOpen} onClose={() => setLandingAlert(false)}>
        <div className={styles.landing_page_modal}>
          <button className={styles.close_button} onClick={() => setLandingAlert(false)}>
            <i className="ico ico-close" />
          </button>
          <div className={styles.modal_content_wrapper}>
            <h2 className={styles.modal_title}>{t('Complete Your Landing Page')}</h2>
            <p className={styles.modal_description}>
              {t('Before you can share your facility publicly, please complete your Landing Page with your facility details, logo, and contact information.')}
            </p>
          </div>
          <ButtonVariant
            title={t('Complete Landing Page')}
            buttonType="primary"
            buttonStyles={styles.button}
            onClickFunc={onClickLandingPage}
          />
        </div>
      </ModalLayout>
    </div>
  );
};

export default Facility;
